/**
 * idb.ts — IndexedDB wrapper (offline-write-sync implementation).
 *
 * Opens the single `marco-offline` database via the `idb` library and exposes
 * the shared DB handle through getDb().
 *
 * Schema — all stores are defined in the upgrade callback below:
 *   reports   keyPath=localId  indexes: by-serverId, by-parentLocalId
 *   outbox    keyPath=localId  index:   by-syncState
 *   blobs     keyPath=id       index:   by-owner (ownerLocalId)
 *   catalogs  keyPath=key
 *   pdfs      keyPath=reportId (v2 — offline backup of generated PDFs)
 */

import { openDB, type IDBPDatabase } from "idb";

const DB_NAME = "marco-offline";
const DB_VERSION = 2;

// Lazy singleton — opened once, shared across all store facades.
let _db: Promise<IDBPDatabase> | null = null;

export function getDb(): Promise<IDBPDatabase> {
  if (!_db) {
    _db = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        // ── reports ───────────────────────────────────────────────────────
        if (!db.objectStoreNames.contains("reports")) {
          const reports = db.createObjectStore("reports", { keyPath: "localId" });
          reports.createIndex("by-serverId", "serverId", { unique: false });
          reports.createIndex("by-parentLocalId", "parentLocalId", { unique: false });
        }

        // ── outbox ────────────────────────────────────────────────────────
        if (!db.objectStoreNames.contains("outbox")) {
          const outbox = db.createObjectStore("outbox", { keyPath: "localId" });
          outbox.createIndex("by-syncState", "syncState", { unique: false });
        }

        // ── blobs ─────────────────────────────────────────────────────────
        // ponytail: Blob stored natively in IDB (no base64). Ceiling = quota;
        // upgrade = prune oldest blobs when quota warning fires.
        if (!db.objectStoreNames.contains("blobs")) {
          const blobs = db.createObjectStore("blobs", { keyPath: "id" });
          blobs.createIndex("by-owner", "ownerLocalId", { unique: false });
        }

        // ── catalogs ──────────────────────────────────────────────────────
        // ponytail: single key-value bag (key='clientes'|'marca'). Ceiling =
        // fine for two small catalogs; upgrade = per-entity stores if queries
        // grow beyond a simple getAll + filter.
        if (!db.objectStoreNames.contains("catalogs")) {
          db.createObjectStore("catalogs", { keyPath: "key" });
        }

        // ── pdfs (v2) ─────────────────────────────────────────────────────
        // Offline backup of server-generated PDFs, keyed by the report's id.
        // ponytail: lazy cache filled when a PDF is viewed online. Ceiling =
        // quota; upgrade = LRU prune when navigator.storage.estimate warns.
        if (!db.objectStoreNames.contains("pdfs")) {
          db.createObjectStore("pdfs", { keyPath: "reportId" });
        }
      },
    });

    // NFR-3: request persistent storage so the OS does not evict our data
    // under low-storage pressure. Best-effort — no throw on denial.
    _db.then(() => {
      if (typeof navigator !== "undefined" && navigator.storage?.persist) {
        void navigator.storage.persist();
      }
    });
  }
  return _db;
}

/**
 * Wipe every object store (without dropping the schema). Called on user change
 * at login so one shared device never leaks reports between accounts —
 * including offline, where no network read would correct a stale cache.
 * Best-effort: never throws.
 */
export async function clearOfflineData(): Promise<void> {
  try {
    const db = await getDb();
    await Promise.all([...db.objectStoreNames].map((name) => db.clear(name)));
  } catch {
    // best-effort — a wipe failure must not block login
  }
}

